import { MailboxDetails } from '../components/MailboxDetails';
import { useEffect, useRef, useState } from 'react';
import type { SessionUser, UserRule } from '../../shared/contracts';
import { api, ApiError } from '../api';
import { applyPreferredFontSize, getPreferredFontSize, type FontSize } from '../font-size';
import type { RulesResponse } from '../types';

interface CalendarColor { id: string; background: string; foreground: string }
interface CalendarAppearance { colorId: string; backgroundColor: string; foregroundColor: string; colors: CalendarColor[] }

const fontSizes = [
  ['small', '小'], ['medium', '中'], ['large', '大'], ['xlarge', '特大'],
] as const;
const ruleFieldLabels: Record<UserRule['field'], string> = {
  sender: '送信者名', email: 'メールアドレス', domain: 'ドメイン', subject: '件名', body: '本文',
};
const ruleGroups: Array<{ kind: UserRule['kind']; label: string; description: string }> = [
  { kind: 'exclude', label: '拾わない', description: '一致したメールを予定にしません' },
  { kind: 'always', label: '必ず拾う', description: '日付があれば必ず予定にします' },
  { kind: 'priority', label: '優先する', description: '予定候補として優先します' },
];
const ruleKindLabels: Record<UserRule['kind'], string> = { exclude: '予定にしない', priority: '優先して拾う', always: '日付があれば必ず拾う' };
const ruleExamples: Record<UserRule['field'], string> = {
  sender: '例：富山商工会議所', email: '例：news@example.com', domain: '例：example.com', subject: '例：楽天SALE', body: '例：配信停止',
};

export function SettingsPage({ user, onSignedOut }: { user: SessionUser; onSignedOut: () => void }) {
  const [duration, setDuration] = useState(user.settings.defaultDurationMinutes);
  const [showPrefix, setShowPrefix] = useState(user.settings.showPrefix);
  const [fontSize, setFontSize] = useState(() => getPreferredFontSize(user.settings.fontSize));
  const [fontSaving, setFontSaving] = useState(false);
  const [rules, setRules] = useState<UserRule[]>([]);
  const [calendar, setCalendar] = useState<CalendarAppearance | null>(null);
  const [calendarError, setCalendarError] = useState('');
  const [calendarNeedsReconnect, setCalendarNeedsReconnect] = useState(false);
  const [savingColor, setSavingColor] = useState(false);
  const [pendingColor, setPendingColor] = useState<string | null>(null);
  const [calendarColorStatus, setCalendarColorStatus] = useState('');
  const colorSaveInFlight = useRef(false);
  const retryColorRef = useRef<string | null>(null);
  const [kind, setKind] = useState<UserRule['kind']>('exclude');
  const [field, setField] = useState<UserRule['field']>('sender');
  const [pattern, setPattern] = useState('');
  const [notice, setNotice] = useState('');
  const [ruleError, setRuleError] = useState('');
  const [editingRule, setEditingRule] = useState<UserRule | null>(null);
  const [ruleBusy, setRuleBusy] = useState(false);
  const fontSaveChain = useRef(Promise.resolve());
  const fontSaveVersion = useRef(0);
  const loadRules = () => api<RulesResponse>('/api/rules').then((value) => setRules(value.rules));
  const loadCalendar = async () => {
    setCalendarError(''); setCalendarNeedsReconnect(false);
    try { setCalendar((await api<{ calendar: CalendarAppearance }>('/api/settings/calendar')).calendar); }
    catch (error) {
      setCalendarError(error instanceof Error ? error.message : '色を読み込めませんでした');
      setCalendarNeedsReconnect(error instanceof ApiError && error.code === 'GOOGLE_REAUTH_REQUIRED');
    }
  };
  useEffect(() => { void Promise.all([loadRules(), loadCalendar()]); }, []);
  const saveSettings = async () => {
    await api('/api/settings', { method: 'PATCH', body: JSON.stringify({ defaultDurationMinutes: duration, showPrefix, fontSize }) });
    setNotice('設定を保存しました'); setTimeout(() => setNotice(''), 2500);
  };
  const changeFontSize = (value: FontSize) => {
    setFontSize(value);
    applyPreferredFontSize(value);
    setFontSaving(true);
    const version = ++fontSaveVersion.current;
    fontSaveChain.current = fontSaveChain.current.catch(() => undefined).then(async () => {
      await api('/api/settings', { method: 'PATCH', body: JSON.stringify({ defaultDurationMinutes: duration, showPrefix, fontSize: value }) });
      if (fontSaveVersion.current === version) {
        setFontSaving(false);
        setNotice('文字サイズを保存しました');
        window.setTimeout(() => setNotice(''), 2500);
      }
    }).catch((error) => {
      if (fontSaveVersion.current === version) {
        setFontSaving(false);
        setNotice(error instanceof Error ? error.message : '文字サイズを保存できませんでした');
      }
    });
  };
  const changeCalendarColor = async (colorId: string) => {
    if (colorSaveInFlight.current || calendar?.colorId === colorId) return;
    colorSaveInFlight.current = true;
    retryColorRef.current = colorId;
    setSavingColor(true); setPendingColor(colorId);
    setCalendarError(''); setCalendarNeedsReconnect(false); setCalendarColorStatus('');
    try {
      const result = await api<{ calendar: CalendarAppearance }>('/api/settings/calendar', { method: 'PATCH', body: JSON.stringify({ colorId }) });
      setCalendar(result.calendar); setCalendarColorStatus('Googleカレンダーの色を変更しました');
      retryColorRef.current = null;
    } catch (error) {
      setCalendarError(error instanceof Error ? error.message : 'Googleカレンダーの色を変更できませんでした');
      setCalendarNeedsReconnect(error instanceof ApiError && error.code === 'GOOGLE_REAUTH_REQUIRED');
    } finally { colorSaveInFlight.current = false; setSavingColor(false); setPendingColor(null); }
  };
  const addRule = async (e: React.FormEvent) => {
    e.preventDefault(); setRuleBusy(true); setRuleError('');
    try {
      await api('/api/rules', { method: 'POST', body: JSON.stringify({ kind, field, pattern }) });
      setPattern(''); await loadRules(); setNotice('条件を追加しました');
    } catch (error) {
      setRuleError(error instanceof Error ? error.message : '条件を追加できませんでした');
    } finally { setRuleBusy(false); }
  };
  const saveRule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRule) return;
    setRuleBusy(true); setRuleError('');
    try {
      await api(`/api/rules/${editingRule.id}`, { method: 'PATCH', body: JSON.stringify({ kind: editingRule.kind, field: editingRule.field, pattern: editingRule.pattern }) });
      setEditingRule(null); await loadRules(); setNotice('条件を変更しました');
    } catch (error) {
      setRuleError(error instanceof Error ? error.message : '条件を変更できませんでした');
    } finally { setRuleBusy(false); }
  };
  const removeRule = async (id: string) => {
    setRuleBusy(true); setRuleError('');
    try { await api(`/api/rules/${id}`, { method: 'DELETE' }); await loadRules(); setNotice('条件を解除しました'); }
    catch (error) { setRuleError(error instanceof Error ? error.message : '条件を解除できませんでした'); }
    finally { setRuleBusy(false); }
  };
  const logout = async () => { await api('/api/auth/logout', { method: 'POST' }); onSignedOut(); };
  const disconnect = async () => {
    if (!window.confirm('Google接続を解除します。自動確認は停止しますが、作成済みのカレンダーとデータはGoogle側に残ります。よろしいですか？')) return;
    await api('/api/auth/disconnect', { method: 'POST' }); onSignedOut();
  };
  return <div className="page settings-page">
    <section className="page-title"><h1>設定</h1></section>
    {notice && <div className="toast inline">{notice}</div>}
    <section className="panel"><h2>予定の登録</h2><div className="form-stack">
      <label>終了時刻がない予定の長さ<select value={duration} onChange={(e) => setDuration(Number(e.target.value) as 30 | 60 | 90 | 120)}>{[30, 60, 90, 120].map((value) => <option key={value} value={value}>{value}分</option>)}</select></label>
      <label className="setting-toggle"><span><strong>タイトルに分類を表示</strong><small>例：🐼【予定】株式会社○○ 打ち合わせ</small></span><input type="checkbox" checked={showPrefix} onChange={(e) => setShowPrefix(e.target.checked)} /></label>
      <button className="button primary" onClick={saveSettings}>設定を保存</button>
    </div></section>
    <section className="panel"><h2>見やすさ</h2><div className="form-stack">
      <div className="field-group"><span className="field-label">文字サイズ</span><div className="font-size-options" role="radiogroup" aria-label="文字サイズ">{fontSizes.map(([value, label]) => <button type="button" key={value} className={fontSize === value ? 'active' : ''} aria-pressed={fontSize === value} onClick={() => changeFontSize(value)} disabled={fontSaving && fontSize === value}>{label}</button>)}</div><small className="font-size-status" role="status">{fontSaving ? '保存中…' : '自動保存'}</small></div>
    </div></section>
    <section className="panel"><h2>Googleカレンダーの色</h2><p>「🐼めーるパンダ」カレンダーに表示する色を選べます。</p>
      <div className="calendar-color-options" role="radiogroup" aria-label="Googleカレンダーの色" aria-busy={savingColor} data-swipe-ignore>{calendar?.colors.map((color) => {
        const selected = (pendingColor ?? calendar.colorId) === color.id;
        return <button type="button" key={color.id} aria-label={`カレンダー色 ${color.id}`} aria-pressed={selected} className={selected ? 'active' : ''} style={{ backgroundColor: color.background, color: color.foreground }} disabled={savingColor} onClick={() => void changeCalendarColor(color.id)}>{selected ? '✓' : ''}</button>;
      })}</div>
      <p className="calendar-color-status" role="status">{savingColor ? '色を保存中…' : calendarColorStatus}</p>
      {!calendar && !calendarError && <p className="muted">色を読み込んでいます…</p>}
      {calendarError && <div className="calendar-load-error" role="alert"><p>{calendarError}</p>{calendarNeedsReconnect ? <a className="button primary" href="/api/auth/google/start">Googleを再接続</a> : <button className="button secondary" onClick={() => void (retryColorRef.current ? changeCalendarColor(retryColorRef.current) : loadCalendar())}>もう一度試す</button>}</div>}
    </section>
    <section className="panel"><h2>メールの判定条件</h2><p>予定カードの「今後拾わない」で追加した条件を確認・解除できます。</p>
      {ruleError && <div className="alert error" role="alert">{ruleError}</div>}
      <details className="manual-rule-details">
        <summary>条件を手動で追加</summary>
        <p>拾い漏らしたくない送信元など、特別な条件が必要な場合だけ使います。</p>
        <form className="rule-form" onSubmit={addRule}>
          <label><span><b>1</b> メールをどうする？</span><select aria-label="動作" value={kind} onChange={(e) => setKind(e.target.value as UserRule['kind'])}><option value="exclude">今後、予定にしない</option><option value="priority">優先して拾う</option><option value="always">日付があれば必ず拾う</option></select></label>
          <label><span><b>2</b> どこにある文字を見る？</span><select aria-label="対象" value={field} onChange={(e) => setField(e.target.value as UserRule['field'])}><option value="sender">送信者名</option><option value="email">メールアドレス</option><option value="domain">メールアドレスの @ 以降</option><option value="subject">件名</option><option value="body">本文</option></select></label>
          <label><span><b>3</b> どの文字を含む？</span><input aria-label="条件の文字" placeholder={ruleExamples[field]} value={pattern} onChange={(e) => setPattern(e.target.value)} required maxLength={200} /></label>
          <div className="rule-preview"><span>この条件の意味</span><strong>{ruleFieldLabels[field]}に「{pattern || '入力した文字'}」を含むメールは、{ruleKindLabels[kind]}。</strong></div>
          <button className="button secondary" disabled={ruleBusy}>{ruleBusy ? '追加中…' : 'この条件を追加'}</button>
        </form>
      </details>
      <div className="rule-summary"><strong>登録済みの条件</strong><span>{rules.length}件</span></div>
      <div className="rule-groups">{ruleGroups.map((group) => {
        const groupedRules = rules.filter((rule) => rule.kind === group.kind);
        if (!groupedRules.length) return null;
        return <details className="rule-group" key={group.kind} open={groupedRules.length <= 5}>
          <summary className="rule-group-heading"><div><h3>{group.label}</h3><small>{group.description}</small></div><span>{groupedRules.length}件</span></summary>
          <div className="rule-list">{groupedRules.map((rule) => editingRule?.id === rule.id
            ? <form className="rule-editor" key={rule.id} onSubmit={saveRule}>
                <select aria-label="動作を編集" value={editingRule.kind} onChange={(e) => setEditingRule({ ...editingRule, kind: e.target.value as UserRule['kind'] })}><option value="exclude">今後拾わない</option><option value="priority">優先する</option><option value="always">日付があれば必ず拾う</option></select>
                <select aria-label="対象を編集" value={editingRule.field} onChange={(e) => setEditingRule({ ...editingRule, field: e.target.value as UserRule['field'] })}><option value="sender">送信者名</option><option value="email">メールアドレス</option><option value="domain">ドメイン</option><option value="subject">件名</option><option value="body">本文</option></select>
                <input aria-label="条件の文字" value={editingRule.pattern} onChange={(e) => setEditingRule({ ...editingRule, pattern: e.target.value })} required maxLength={200} autoFocus />
                <div><button type="button" className="button ghost" onClick={() => setEditingRule(null)} disabled={ruleBusy}>戻る</button><button className="button primary" disabled={ruleBusy}>{ruleBusy ? '保存中…' : '保存'}</button></div>
              </form>
            : <div key={rule.id}><p><strong>{ruleFieldLabels[rule.field]}</strong><span>「{rule.pattern}」を含む</span></p><div className="rule-actions"><button className="rule-edit" onClick={() => { setEditingRule({ ...rule }); setRuleError(''); }} disabled={ruleBusy}>編集</button><button onClick={() => void removeRule(rule.id)} aria-label={`${rule.pattern}の条件を解除`} disabled={ruleBusy}>解除</button></div></div>)}</div>
        </details>;
      })}{rules.length === 0 && <p className="muted">まだ条件はありません。予定カードの「今後拾わない」から追加できます。</p>}</div>
    </section>
    <MailboxDetails user={user} />
    <section className="panel data-policy" aria-labelledby="data-policy-title"><h2 id="data-policy-title">データの保存とプライバシー</h2>
      <h3>メールや予定の内容を、通常の運用で閲覧しません</h3>
      <p>通常のサービス運用で、コンプオフィスの担当者がお客様のメール本文や予定・カレンダーの内容を閲覧することはありません。お問い合わせの際も、内容の共有が必要な場合は事前にお客様の同意をいただき、必要な範囲に限って確認します。</p>
      <h3>予定のデータは、お客様のGoogleアカウントに保存します</h3>
      <p>予定の内容・判定理由・メールの抽出条件は、お客様ご自身のGoogleスプレッドシートに保存し、予定はお客様のGoogleカレンダーに登録します。元のメールはGmailに残ります。</p>
      <p>運営側のCloudflareには、サービスの提供・認証・障害対応に必要な氏名・メールアドレス・暗号化した接続情報・設定・利用量・処理状況などを保存します。メール本文や予定の内容を運営側のデータベースに保存することはありません。</p>
      <h3>アプリの自動処理とAIへの送信について</h3>
      <p>担当者による閲覧とは別に、アプリはお客様がGoogleで許可した範囲のメール・シート・カレンダーを自動処理します。予定の判定に必要な送信者・件名・受信日時・本文の一部などは、運営側のCloudflareを経由してGoogleのGemini APIへ送信します。個人情報が含まれる場合があります。Google・Cloudflareでの取扱いには、各サービスの規約・プライバシーポリシーが適用されます。</p>
      <h3>送信した情報は、AIの学習・改善に利用されません</h3>
      <p>予定の判定には、Google Gemini 3.5 Flash-Liteの有料APIを使用しています。Googleの有料APIの利用条件に基づき、<strong>送信したメールの情報や判定結果が、GoogleのAIモデルの学習・改善に利用されることはありません。</strong>コンプオフィスから学習用データとして提供することもありません。</p>
      <p>ただし、不正利用の検知・防止、安全性の確保や法令上必要な対応のため、Google側で送信内容や判定結果が一定期間保存される場合があります。「学習に利用されない」ことは、「Google側に一切保存されない」ことを意味しません。</p>
      <p>コンプオフィスは、取得した情報を本サービスの提供・保守・お問い合わせ対応に必要な範囲で利用し、広告配信や販売の目的には利用しません。</p>
      <p><a href="https://policies.google.com/privacy?hl=ja" target="_blank" rel="noopener noreferrer">Googleのプライバシーポリシー</a> ／ <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer">Gemini APIの利用規約</a> ／ <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">Cloudflareのプライバシーポリシー</a></p>
      <h3>自動整理・接続解除・データの削除</h3>
      <p>自動整理が有効な場合、予定日から3か月経過した予定は年別タブへ移動し、6か月経過した予定詳細は確認用コピーを7日間残して削除します。整理によってGoogleカレンダーの予定は消えません。必要な記録は、整理前にご自身で別途保存してください。</p>
      <p>下の「Google接続を解除」で、自動確認を停止できます。解除しても作成済みのカレンダー・スプレッドシートや運営側の利用者情報・運用記録は自動削除されません。Google側のデータや共有設定は、ご自身で管理・削除してください。運営側に保存する情報の削除については、コンプオフィスへお問い合わせください。</p>
    </section>
    <section className="panel data-policy" aria-labelledby="usage-notice-title"><h2 id="usage-notice-title">ご利用にあたってのお願い・注意事項</h2>
      <h3>大切な予定・締切は、必ず元のメールで確認してください</h3>
      <p>めーるパンダは予定管理を補助するサービスです。AIの判定には、読み違い・登録漏れ・重複・日時や変更内容の誤りが生じることがあり、正確性・完全性や期限内の登録を保証するものではありません。「要確認」の予定や候補日は、出席・予約・契約などが確定したことを意味しません。</p>
      <p>支払い・契約更新・予約・業務上の締切など、重要な事項は元のメールと照合し、ご自身でも確認・管理してください。予定の登録だけで、予約・返信・支払いなどの手続きが行われることはありません。</p>
      <h3>通信障害や接続切れで、処理が止まる場合があります</h3>
      <p>Google・Gemini・Cloudflareの障害や仕様変更、利用上限、認証の有効期限などにより、登録の遅延や停止が発生する場合があります。継続的な稼働や通知の到達を保証するものではありません。アプリの確認状況を定期的にご確認ください。</p>
      <h3>アカウントと共有範囲をご確認ください</h3>
      <p>Googleアカウントを適切に管理し、スプレッドシートやカレンダーを共有すると、その相手にも内容が見えることにご注意ください。勤務先・お取引先などの機密情報や第三者の個人情報を扱う場合は、所属先のルールや必要な権限・同意を確認してご利用ください。</p>
      <h3>責任の取扱いについて</h3>
      <p>誤判定・登録漏れ・遅延などによる不利益を防ぐため、本サービスだけに依存せず、重要な予定はご自身でも管理してください。コンプオフィスの責任は、消費者契約法その他の適用法令に従います。本記載は、当方の故意・重過失による責任や、法令上免除・制限できない責任を免除・制限するものではありません。</p>
    </section>
    <section className="panel account-panel"><h2>Google接続</h2><p>{user.email}</p><div><button className="button ghost" onClick={logout}>ログアウト</button><button className="button danger-text" onClick={disconnect}>Google接続を解除</button></div></section>
  </div>;
}
